import { Capacitor } from '@capacitor/core';

import { createCapacitorPreferencesAdapter } from './adapters/capacitorPreferences';
import { createLocalStorageAdapter } from './adapters/localStorage';
import { getJsonFromAdapter, setJsonOnAdapter } from './jsonHelpers';
import type { AsyncStorageLike, StorageAdapter } from './types';

/** Reserved meta key for the Preferences managed-key registry. */
export const PREFERENCES_MANAGED_KEYS_META = 'apexx.storage.preferences.managedKeys';

export type PreferencesOptions = {
	/** Inject a backend (tests). When omitted, picks Capacitor Preferences vs localStorage. */
	adapter?: StorageAdapter;
};

function defaultPreferencesAdapter(): StorageAdapter {
	if (Capacitor.isNativePlatform()) {
		return createCapacitorPreferencesAdapter();
	}

	return createLocalStorageAdapter();
}

/**
 * Small / sensitive async KV.
 * `clear()` / `keys()` are scoped via a managed-key registry — never wipe the whole backend.
 * Production logout must use `removeItem`; `clear()` is for tests / reset tooling only.
 */
export class Preferences implements AsyncStorageLike {
	readonly #adapter: StorageAdapter;
	#readyPromise: Promise<void> | null = null;

	constructor(options: PreferencesOptions = {}) {
		this.#adapter = options.adapter ?? defaultPreferencesAdapter();
	}

	/** Exposes the underlying adapter for tests. */
	get adapter(): StorageAdapter {
		return this.#adapter;
	}

	async ready(): Promise<void> {
		if (!this.#readyPromise) {
			this.#readyPromise = Promise.resolve();
		}

		return this.#readyPromise;
	}

	async getItem(key: string): Promise<string | null> {
		return this.#adapter.getItem(key);
	}

	async setItem(key: string, value: string): Promise<void> {
		await this.#adapter.setItem(key, value);
		if (key !== PREFERENCES_MANAGED_KEYS_META) {
			await this.#registerKey(key);
		}
	}

	async removeItem(key: string): Promise<void> {
		await this.#adapter.removeItem(key);
		if (key !== PREFERENCES_MANAGED_KEYS_META) {
			await this.#unregisterKey(key);
		}
	}

	async clear(): Promise<void> {
		const managed = await this.#readManagedKeys();
		for (const key of managed) {
			await this.#adapter.removeItem(key);
		}
		await this.#adapter.removeItem(PREFERENCES_MANAGED_KEYS_META);
	}

	async keys(): Promise<string[]> {
		return this.#readManagedKeys();
	}

	async getJson<T>(key: string): Promise<T | null> {
		return getJsonFromAdapter<T>(this.#adapter, key);
	}

	async setJson(key: string, value: unknown): Promise<void> {
		await setJsonOnAdapter(this.#adapter, key, value);
		if (key !== PREFERENCES_MANAGED_KEYS_META) {
			await this.#registerKey(key);
		}
	}

	async #readManagedKeys(): Promise<string[]> {
		const raw = await this.#adapter.getItem(PREFERENCES_MANAGED_KEYS_META);
		if (!raw) {
			return [];
		}

		try {
			const parsed = JSON.parse(raw) as unknown;
			if (!Array.isArray(parsed)) {
				return [];
			}

			return parsed.filter((item): item is string => typeof item === 'string');
		} catch {
			return [];
		}
	}

	async #writeManagedKeys(keys: string[]): Promise<void> {
		await this.#adapter.setItem(PREFERENCES_MANAGED_KEYS_META, JSON.stringify(keys));
	}

	async #registerKey(key: string): Promise<void> {
		const managed = await this.#readManagedKeys();
		if (managed.includes(key)) {
			return;
		}
		managed.push(key);
		await this.#writeManagedKeys(managed);
	}

	async #unregisterKey(key: string): Promise<void> {
		const managed = await this.#readManagedKeys();
		const next = managed.filter((item) => item !== key);
		if (next.length === managed.length) {
			return;
		}
		if (next.length === 0) {
			await this.#adapter.removeItem(PREFERENCES_MANAGED_KEYS_META);

			return;
		}
		await this.#writeManagedKeys(next);
	}
}

let defaultPreferences: Preferences | null = null;

/** Shared Preferences singleton for app code. */
export function getPreferences(): Preferences {
	if (!defaultPreferences) {
		defaultPreferences = new Preferences();
	}

	return defaultPreferences;
}

/** @deprecated Prefer {@link getPreferences} — kept as a convenient alias. */
export const preferences = {
	getItem: (key: string) => getPreferences().getItem(key),
	setItem: (key: string, value: string) => getPreferences().setItem(key, value),
	removeItem: (key: string) => getPreferences().removeItem(key),
	clear: () => getPreferences().clear(),
	keys: () => getPreferences().keys(),
	getJson: <T>(key: string) => getPreferences().getJson<T>(key),
	setJson: (key: string, value: unknown) => getPreferences().setJson(key, value),
	ready: () => getPreferences().ready(),
} satisfies AsyncStorageLike;

/** @internal */
export function setPreferencesForTests(instance: Preferences | null): void {
	defaultPreferences = instance;
}
